// src/contexts/UserProfileContext.js
import React, { createContext, useState, useEffect, useCallback } from 'react';
import { isValidAccessKey } from '../utils/accessKeyUtils';
import {
  pruneAccessHistory,
  slimAccessesMap,
  emergencySlimProfile,
} from '../utils/accessStorage';

// create the context
export const UserProfileContext = createContext(null);

const DEFAULT_PROFILE = {
  given_name: '',
  family_name: '',
  email: '',
  company: '',
  phone: '',
  field_engineer_id: null,
  accesses: {},
  access_history: []
};

/**
 * Sanitizes user profile data loaded from localStorage.
 * Filters out invalid access keys and ensures correct data structure.
 */
function sanitizeUserProfile(data) {
  if (!data || typeof data !== 'object') {
    return { ...DEFAULT_PROFILE };
  }

  // Filter accesses to only include valid access keys
  const validAccesses = {};
  if (data.accesses && typeof data.accesses === 'object') {
    Object.entries(data.accesses).forEach(([key, value]) => {
      if (isValidAccessKey(key) && value && typeof value === 'object') {
        validAccesses[key] = value;
      } else if (!isValidAccessKey(key)) {
        console.warn('Filtering out invalid access key from localStorage');
      }
    });
  }

  return {
    ...DEFAULT_PROFILE,
    ...data,
    accesses: slimAccessesMap(validAccesses),
    access_history: pruneAccessHistory(
      Array.isArray(data.access_history) ? data.access_history : []
    ),
  };
}

// provider component
export function UserProfileProvider({ children }) {
  // read localStorage on init with error handling and sanitization
  const [userProfile, setUserProfile] = useState(() => {
    try {
      const saved = window.localStorage.getItem('userProfile');
      return saved ? sanitizeUserProfile(JSON.parse(saved)) : { ...DEFAULT_PROFILE };
    } catch (e) {
      console.error('Failed to parse userProfile from localStorage:', e);
      return { ...DEFAULT_PROFILE };
    }
  });
  

  // controls if we show the modal
  const [showProfileModal, setShowProfileModal] = useState(false);

  // Session-only set of access keys that are running a User Workflow. For
  // those access keys, the profile modal must never be shown — User Workflows
  // are designed to be anonymous.
  const [userWorkflowAccessKeys, setUserWorkflowAccessKeys] = useState(() => {
    try {
      const saved = window.sessionStorage.getItem('userWorkflowAccessKeys');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  // whenever userProfile changes, persist to localStorage
  useEffect(() => {
    const persist = (profile) => {
      window.localStorage.setItem('userProfile', JSON.stringify(profile));
    };

    try {
      persist(userProfile);
    } catch (err) {
      const isQuota =
        err instanceof DOMException &&
        (err.name === 'QuotaExceededError' || err.code === 22);
      if (!isQuota) {
        console.error('Failed to persist userProfile to localStorage:', err);
        return;
      }

      console.warn('userProfile localStorage quota exceeded; slimming and retrying');
      try {
        persist(emergencySlimProfile(userProfile));
      } catch (retryErr) {
        console.warn('Could not persist slimmed userProfile:', retryErr);
      }
    }
  }, [userProfile]);

  useEffect(() => {
    try {
      window.sessionStorage.setItem(
        'userWorkflowAccessKeys',
        JSON.stringify(userWorkflowAccessKeys)
      );
    } catch {
      // sessionStorage may be unavailable; non-fatal
    }
  }, [userWorkflowAccessKeys]);

  const markUserWorkflowAccess = useCallback((access_key) => {
    if (!access_key) return;
    setUserWorkflowAccessKeys((prev) =>
      prev[access_key] ? prev : { ...prev, [access_key]: true }
    );
  }, []);

  const isUserWorkflowAccess = useCallback(
    (access_key) => !!(access_key && userWorkflowAccessKeys[access_key]),
    [userWorkflowAccessKeys]
  );

  const updateUserProfile = useCallback((updates) => {
    setUserProfile((prev) => ({ ...prev, ...updates }));
  }, []);

  // function to clear userProfile (log out)
  function clearUserProfile() {
    setUserProfile({
      given_name: '',
      family_name: '',
      email: '',
      company: '',
      phone: '',
      field_engineer_id: null,
      accesses: {}, // Reset accesses to an empty map on logout
      access_history: [] // Reset access_history on logout
    });
    window.localStorage.removeItem('userProfile');
  }

  // Function to add a new access object to the accesses map
  function addAccessKey(newAccess) {
    setUserProfile((prev) => {
      const existingAccess = prev.accesses[newAccess.access_key];

      if (existingAccess) {
        if (new Date(newAccess.expiry_date) > new Date(existingAccess.expiry_date)) {
          return {
            ...prev,
            accesses: {
              ...prev.accesses,
              [newAccess.access_key]: newAccess,
            },
          };
        } else {
          return prev;
        }
      }

      return {
        ...prev,
        accesses: {
          ...prev.accesses,
          [newAccess.access_key]: newAccess,
        },
      };
    });
  }

  // Utility function to get accesses as an array
  function getAccessesArray() {
    return Object.values(userProfile.accesses);
  }

  // Check if profile is complete
  function isProfileComplete() {
    return !!(userProfile.given_name && userProfile.family_name && userProfile.email && userProfile.company && userProfile.phone);
  }

  // Get current access key from URL or profile
  function getCurrentAccessKey() {
    // First try to get from URL path
    const pathMatch = window.location.pathname.match(/\/assets\/access_key\/([^/]+)/);
    if (pathMatch) {
      return pathMatch[1];
    }
    
    // If not in URL, check if we have any valid access keys in profile
    const accessKeys = Object.keys(userProfile.accesses);
    if (accessKeys.length > 0) {
      // Return the most recent access key (you might want to implement better logic here)
      return accessKeys[accessKeys.length - 1];
    }
    
    return null;
  }

  // Check if we have a valid access key
  function hasValidAccessKey() {
    const accessKey = getCurrentAccessKey();
    return !!accessKey;
  }

  return (
    <UserProfileContext.Provider
      value={{
        userProfile,
        showProfileModal,
        setShowProfileModal,
        updateUserProfile,
        clearUserProfile,
        addAccessKey,
        getAccessesArray, // Provide utility function in context
        isProfileComplete,
        getCurrentAccessKey,
        hasValidAccessKey,
        userWorkflowAccessKeys,
        markUserWorkflowAccess,
        isUserWorkflowAccess,
      }}
    >
      {children}
    </UserProfileContext.Provider>
  );
}
